import { useEffect, useRef, useState } from 'react';

/**
 * Shared chassis for the FILES / NOTES tabs (Phase 3f — inventory §B4/§B5).
 * Both Backbone views share the same split-panel template family
 * (files.tmpl / notes.tmpl), the same delete-popup markup
 * (files|notes/tmpl/delete_popup.tmpl) and the same dropdown-position hack.
 *
 * [NEG]s honored here (parity 175/181):
 *  - the "Reset Use Defined Field?" popup mode (icon-click className accident;
 *    its Reset button has NO handler — dead branch) is NOT built: every click
 *    on the Delete item opens the DELETE mode;
 *  - dead events-hash entries (`blur .content-title` → saveFileFieldData /
 *    saveNotesFieldData, `click #cancel` → cancel, `click .note-input` →
 *    click_note_name) — methods don't exist in Backbone, nothing to port;
 *  - the commented-out `blur .note-input` → blur_note_name re-readonly binding
 *    stays unported (the sidebar rename input is simply always writable, as
 *    the live template ships it — no readonly attr).
 */

/**
 * Delete confirm popup — files/tmpl/delete_popup.tmpl & notes twin, DELETE
 * mode only. Exact chassis: `#popup-content.popup-wrapper.width-small.active`;
 * Esc closes via the global popup-keys handler (button[name=cancel_delete]).
 */
export const FilesNotesDeletePopup = ({
  title,
  content,
  name,
  onConfirm,
  onClose,
}: {
  title: string;
  content: string;
  name: string | null | undefined;
  onConfirm: () => void;
  onClose: () => void;
}) => (
  <div id="popup-content" className="popup-wrapper width-small active">
    <div className="popup-content">
      <h2 className="popup-heading">{title}</h2>
      <div className="popup-content-part">
        <p>{content}</p>
        <ul className="deleted-items">
          <li>
            <p>{name ?? ''}</p>
          </li>
        </ul>
      </div>
      <div className="popup-control-buttons">
        <button
          className="btn not-highlighted-btn"
          name="confirm_delete"
          onClick={onConfirm}
        >
          Delete
        </button>
        <button className="btn highlighted-btn" name="cancel_delete" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  </div>
);

/**
 * Per-row "More Actions" dropdown (`.dropdown_files_fields` /
 * `.dropdown_notes_fields`). The legacy `show.bs.dropdown` → `position()`
 * pins every menu near its button (files: +30px top / −110px left; notes:
 * +32px / −145px — jQuery offset() math). Reproduced with position:fixed off
 * the button rect (same visual placement; robust to container scroll).
 * Bootstrap shows the menu via `.open` on the `.dropdown`; any outside click
 * closes it (document listener).
 *
 * `openOnHover` is a deliberate deviation from legacy (requested for the NOTES
 * rail): the menu opens on pointer-enter instead of waiting for the click.
 * Legacy — and the FILES rail, which passes nothing — is click-only Bootstrap.
 * With it on, the click still OPENS (it no longer toggles) so that clicking the
 * ⋮ you just hovered does not dismiss the menu under the cursor; touch and
 * keyboard therefore keep a way in.
 */
export const RowActionsDropdown = ({
  id,
  dropdownClassName,
  topOffset,
  leftOffset,
  disabled,
  openOnHover = false,
  children,
}: {
  id: number;
  dropdownClassName: string;
  topOffset: number;
  leftOffset: number;
  disabled: boolean;
  openOnHover?: boolean;
  children: (close: () => void) => React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  /** Pin the menu to the button, as the legacy `show.bs.dropdown` hook does. */
  const position = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.top + topOffset, left: rect.left + leftOffset });
  };

  // The menu is a DOM child of the wrapper but position:fixed 145px to its
  // left, so travelling from the ⋮ to the menu crosses dead space and fires a
  // leave. Closing on a short delay keeps it open across that gap; re-entering
  // either element cancels it.
  const hoverProps = openOnHover
    ? {
        onMouseEnter: () => {
          if (disabled) return;
          cancelClose();
          position();
          setOpen(true);
        },
        onMouseLeave: () => {
          cancelClose();
          closeTimer.current = setTimeout(() => setOpen(false), 250);
        },
      }
    : {};

  return (
    <div ref={wrapRef} className={`dropdown ${dropdownClassName}`} {...hoverProps}>
      {/* `.open` goes on the menu's immediate parent — Bootstrap 3's
          `.open > .dropdown-menu` show rule (the legacy data-toggle adds it
          to the button's parent div, AhuRow precedent). */}
      <div className={open ? 'open' : ''}>
        <button
          ref={btnRef}
          className={`ka-has-tooltip btn btn-udf-delete dropdown-toggle icon-more-actions${
            disabled ? ' non-clickable' : ''
          }`}
          type="button"
          data-toggle="dropdown"
          data-id={id}
          tooltip="More Actions"
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            cancelClose();
            position();
            setOpen((o) => (openOnHover ? true : !o));
          }}
        />
        <ul
          className="dropdown-menu"
          style={pos ? { position: 'fixed', top: pos.top, left: pos.left } : undefined}
        >
          {children(() => setOpen(false))}
        </ul>
      </div>
    </div>
  );
};

/**
 * Upload-error message extractor (uploadPDF / addCheckFile error paths).
 * The Backbone code reads `resp.pdf_file.message` — which covers the
 * FileValidator 400s (`{'pdf_file': {'message': ['File format must be pdf.' |
 * 'Maximum file size of {n}MB exceeded']}}`, array string-concatenated into
 * the span) but CRASHES on the library-cap shape (`{'message': 'Maximum
 * library size exceeded.'}` — no pdf_file key, preloader stuck). Parity 170
 * explicitly expects the library-cap message displayed, so the extractor is
 * tolerant of both shapes (+ plain field-error arrays).
 */
export function extractPdfErrorMessage(err: unknown): string {
  const data = (err as { data?: unknown } | null)?.data as
    | Record<string, unknown>
    | undefined;
  const pdfField = data?.pdf_file as
    | { message?: unknown }
    | unknown[]
    | string
    | undefined;
  let msg: unknown;
  if (pdfField && typeof pdfField === 'object' && !Array.isArray(pdfField)) {
    msg = (pdfField as { message?: unknown }).message;
  }
  if (msg == null) msg = Array.isArray(pdfField) ? pdfField : undefined;
  if (msg == null && typeof pdfField === 'string') msg = pdfField;
  if (msg == null) msg = data?.message;
  if (Array.isArray(msg)) msg = msg.join(',');
  return msg == null ? 'Upload failed' : String(msg);
}

/**
 * Per-file cap for Project Board -> Files uploads, in MB. The sidebar hint has
 * always quoted 10 MB and that is the intended limit.
 *
 * Deliberately a constant and not read from the server (`GET /project/limit/`
 * publishes `project_pdf_file_size_max_in_mb`, but it 500s — its serializer
 * declares no `fields` — and the row says 30). A frontend-only fix was the call
 * in the thread, so the server keeps enforcing its own, looser value as a
 * backstop: nothing a user can do through the UI reaches it.
 */
export const PDF_SIZE_LIMIT_MB = 10;

/**
 * Pre-flight size gate, worded like the server's own rejection
 * (`FileValidator`: `"Maximum file size of {n}MB exceeded"`, MB counted as
 * 1_000_000 bytes, not 1 MiB).
 *
 * It has to happen before the request, not after: nginx
 * (`client_max_body_size`) and Cloudflare answer 413 with an HTML page while
 * the browser is still writing the multipart body, so a large PDF produced no
 * validation message at all and `PATCH /project/pdf/<id>` sat pending. Checking
 * `File.size` first shows the cap immediately and keeps a several-hundred-MB
 * body off the wire.
 */
export function pdfSizeLimitError(file: File): string | null {
  if (file.size <= PDF_SIZE_LIMIT_MB * 1000000) return null;
  return `Maximum file size of ${PDF_SIZE_LIMIT_MB}MB exceeded`;
}

/**
 * The legacy 1s-delayed title focus (`setTimeout(... .focus(), 1000)` in
 * changeFilesFild / changeNotesFild / active_last_change). Timers are never
 * cleared in Backbone either; the element lookup is null-safe.
 */
export function scheduleTitleFocus(domId: string): void {
  setTimeout(() => {
    document.getElementById(domId)?.focus();
  }, 1000);
}

/**
 * Caret-to-end on focus — the inline
 * `onfocus="let value = this.value; this.value = null; this.value=value"`
 * hack on the right-pane title inputs (files.tmpl / notes.tmpl), and the
 * rename_note `input.val('');input.val(val)` variant.
 */
export function caretToEnd(el: HTMLInputElement): void {
  const { value } = el;
  el.value = '';
  el.value = value;
}
